import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { VolumeCountResponse } from '~/shared/types/api';
import { useAuthContext } from '~/shared/context/useAuthContext';
import { getApiFilters, splitAccountFilters } from '~/shared/utils/apiFilters';
import { appendAccountsScope, getVolumeDataQueryScopeParts } from '~/shared/utils/organizationUtils';
import { useXFetch } from '~/shared/utils/useXFetch';
import { SelectOption } from '~/shared/utils/types';
import {
  VOLUME_VIEW_PRESETS,
  VolumeViewPresetId,
  VolumeViewPreset,
} from '~/shared/utils/volumeViewPresets';
import {
  createAutoscalingStateFilters,
  createEligibilityFilters,
  stripAutoscalingFiltersFromApiFilters,
} from '~/shared/utils/autoscalingFilterUtils';

const VOLUMES_BASE_URL = '/api/v1/volumes';

function buildFilterStringsForPreset(
  preset: VolumeViewPreset,
  baseFiltersWithoutAutoscaling: SelectOption[]
): string[] {
  const combinedFilters = [
    ...baseFiltersWithoutAutoscaling,
    ...createAutoscalingStateFilters(preset.stateIds),
    ...createEligibilityFilters(preset.eligibilityIds),
  ];
  const parsedFiltersResult = getApiFilters(combinedFilters);
  return Array.isArray(parsedFiltersResult) ? parsedFiltersResult : [parsedFiltersResult];
}

export function useVolumeViewPresetCounts(baseFilters: SelectOption[]): {
  presetCounts: Partial<Record<VolumeViewPresetId, number>>;
  isLoading: boolean;
} {
  const api = useXFetch();
  const { accounts, activeTenantId } = useAuthContext();

  const { accountIds, filtersWithoutAccount } = useMemo(
    () => splitAccountFilters(baseFilters),
    [baseFilters]
  );

  const baseFiltersWithoutAutoscaling = useMemo(
    () => stripAutoscalingFiltersFromApiFilters(filtersWithoutAccount),
    [filtersWithoutAccount]
  );

  const volumeScopeKey = useMemo(
    () => getVolumeDataQueryScopeParts(accounts, activeTenantId, accountIds),
    [accounts, activeTenantId, accountIds]
  );

  const query = useQuery({
    queryKey: [
      'volume-view-preset-counts',
      JSON.stringify({
        baseFiltersWithoutAutoscaling,
        activeTenantId,
        volumeScopeKey: [...volumeScopeKey],
      }),
    ],
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const counts = await Promise.all(
        VOLUME_VIEW_PRESETS.map(async (preset) => {
          const filterParams = buildFilterStringsForPreset(preset, baseFiltersWithoutAutoscaling);
          const queryParams = new URLSearchParams();
          filterParams.forEach((filter) => {
            queryParams.append('filters', filter);
          });
          appendAccountsScope(queryParams, accounts, activeTenantId, accountIds);
          const response = await api<VolumeCountResponse>(
            `${VOLUMES_BASE_URL}/count?${queryParams.toString()}`
          );
          return [preset.id, response?.count ?? 0] as const;
        })
      );

      return Object.fromEntries(counts) as Record<VolumeViewPresetId, number>;
    },
  });

  return {
    presetCounts: query.data ?? {},
    isLoading: query.isLoading && !query.data,
  };
}
